/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { PencilIcon } from '@heroicons/react/24/solid'
import { Company } from 'types/entities/Company'
import { getPrimaryCompanyAddress, getPrimaryCompanyContact } from 'utils/companiesUtils'

interface CompanyBasicDuplicateItemProps {
    dup: Company
    onDuplicateClick?: (company: Company) => void
}

const CompanyBasicDuplicateItem: React.FC<CompanyBasicDuplicateItemProps> = ({ dup, onDuplicateClick }) => {
    const address = getPrimaryCompanyAddress(dup)
    const contact = getPrimaryCompanyContact(dup)

    return (
        <li
            key={dup.id}
            className="group flex flex-col p-2 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 border border-amber-200/50 dark:border-amber-800/50 rounded-lg transition-all duration-200 cursor-pointer"
            onClick={() => onDuplicateClick?.(dup)}
        >
            <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-amber-950 dark:text-amber-200 truncate text-[10px]">
                    {`${dup.id} • ${dup.name}`}
                </div>
                <PencilIcon className="w-3 h-3 text-amber-700 dark:text-amber-400" />
            </div>
            <div className="flex flex-col text-[9px] text-amber-800/70 dark:text-amber-400/70 leading-tight">
                <span className="truncate">
                    {[address?.town, address?.postal_code, address?.street_name, address?.region]
                        .filter((v) => !!v && `${v}`.trim().length > 0)
                        .join(' • ')}
                </span>
                <span className="truncate">
                    {[
                        [dup.first_name, dup.last_name].filter((v) => !!v).join(' '),
                        contact?.email ?? dup.email,
                        contact?.phone ?? dup.phone,
                        dup.cin,
                    ]
                        .filter((v) => !!v && `${v}`.trim().length > 0)
                        .join(' • ')}
                </span>
            </div>
        </li>
    )
}

interface CompanyBasicDuplicatesListProps {
    duplicates: Company[]
    onDuplicateClick?: (company: Company) => void
}

const CompanyBasicDuplicatesList: React.FC<CompanyBasicDuplicatesListProps> = ({ duplicates, onDuplicateClick }) => {
    if (!duplicates.length) {
        return null
    }

    return (
        <div className="CompanyBasicDuplicatesList">
            {duplicates.map((dup) => (
                <CompanyBasicDuplicateItem key={dup.id} dup={dup} onDuplicateClick={onDuplicateClick} />
            ))}
        </div>
    )
}

export default CompanyBasicDuplicatesList
